// Premium Feature Gate - Locks features behind subscription
// Shows dummy/disabled fields with upgrade prompts for free users
// Last Updated: October 2025

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { SubscriptionManager } from '../SubscriptionManager.js';
import { trackPricingOpened, trackPricingPlanClick, trackCheckoutStarted } from '../utils/funnelAnalytics.js';
import { normalizePricingContext, openPricingModal } from '../utils/openPricingModal.js';
import { getCheckoutAttribution } from '../utils/attribution.js';
import { USER_TESTIMONIALS } from '../data/userTestimonials.js';

/** Sources where the user already hit a paywall/gate — keep modal minimal. */
const HOT_CHECKOUT_SOURCES = new Set([
    'post_result',
    'post_result_dismissed',
    'advanced_gate',
    'pdf_export',
    'saved_scenario',
    'advanced_demo',
    'free_runs_banner',
]);

// Lazy load SubscriptionManagement to avoid circular dependencies
const SubscriptionManagement = lazy(() => import('./SubscriptionManagement.js').then(module => ({ default: module.SubscriptionManagement || module.default })));

// Premium Feature Wrapper - Locks content behind paywall
export const PremiumFeatureGate = ({ 
    feature, 
    children, 
    showPreview = true,
    previewMessage = null 
}) => {
    // Sync read for initial render; re-read after mount so we never rely on a stale/race read (BASIC_WHATIFS must stay locked for free)
    const [hasAccess, setHasAccess] = useState(() => SubscriptionManager.hasAccess(feature));
    useEffect(() => {
        setHasAccess(SubscriptionManager.hasAccess(feature));
    }, [feature]);
    const [paywallEmail, setPaywallEmail] = useState('');
    const [paywallSubmitting, setPaywallSubmitting] = useState(false);
    const [paywallSubmitted, setPaywallSubmitted] = useState(false);
    const [paywallError, setPaywallError] = useState(null);

    const handlePaywallEmailSubmit = async (e) => {
        e.preventDefault();
        if (!paywallEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paywallEmail)) {
            setPaywallError('Please enter a valid email.');
            return;
        }
        setPaywallError(null);
        setPaywallSubmitting(true);
        try {
            const res = await fetch('/.netlify/functions/capture-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: paywallEmail.trim().toLowerCase(),
                    calculatorType: 'basic',
                    source: 'paywall_checklist'
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed');
            setPaywallSubmitted(true);
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'email_capture_success', { event_category: 'Email Capture', event_label: 'Paywall checklist', source: 'paywall_checklist', value: 1 });
            }
        } catch (err) {
            setPaywallError(err.message || 'Something went wrong. Try again.');
        } finally {
            setPaywallSubmitting(false);
        }
    };

    if (hasAccess) {
        return <>{children}</>;
    }

    // Free user sees locked content
    return (
        <div className="relative">
            {/* Blurred/Disabled Preview */}
            {showPreview && (
                <div className="pointer-events-none opacity-40 select-none">
                    {children}
                </div>
            )}
            
            {/* Overlay with Upgrade Prompt */}
            <div className={`${showPreview ? 'absolute inset-0' : ''} flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 ${showPreview ? 'bg-opacity-95' : 'p-8 rounded-lg'}`}>
                <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-xl border-2 border-blue-200">
                    <div className="text-5xl mb-4">🔒</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Advanced Retirement Feature
                    </h3>
                    <p className="text-gray-700 mb-4">
                        {previewMessage || SubscriptionManager.getUpgradeMessage(feature)}
                    </p>
                    <UpgradeButton />
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Not ready to subscribe?</p>
                        {paywallSubmitted ? (
                            <p className="text-sm text-green-600 font-medium">Check your email for the free checklist.</p>
                        ) : (
                            <form onSubmit={handlePaywallEmailSubmit} className="flex flex-col gap-2">
                                <input
                                    type="email"
                                    value={paywallEmail}
                                    onChange={(e) => setPaywallEmail(e.target.value)}
                                    placeholder="Email for free checklist"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    disabled={paywallSubmitting}
                                />
                                <button type="submit" disabled={paywallSubmitting} className="w-full py-2 text-sm font-semibold text-purple-600 border border-purple-300 rounded-md hover:bg-purple-50 disabled:opacity-50">
                                    {paywallSubmitting ? 'Sending…' : 'Get free retirement checklist'}
                                </button>
                                {paywallError && <p className="text-xs text-red-600">{paywallError}</p>}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Locked Input Field - Shows dummy data but can't be edited
export const LockedInput = ({ 
    label, 
    value, 
    dummyValue = '•••', 
    feature,
    tooltip = null,
    className = ''
}) => {
    const hasAccess = SubscriptionManager.hasAccess(feature);
    
    if (hasAccess) {
        // Return null - parent component will render actual input
        return null;
    }

    return (
        <div className={`relative ${className}`}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    Advanced
                </span>
            </label>
            <div className="relative">
                <input
                    type="text"
                    value={dummyValue}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-400 cursor-not-allowed"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                </div>
            </div>
            {tooltip && (
                <p className="mt-1 text-xs text-gray-500">{tooltip}</p>
            )}
        </div>
    );
};

// Locked Section - Shows feature preview with overlay
export const LockedSection = ({ 
    title, 
    description, 
    feature,
    icon = '🔒',
    children 
}) => {
    const hasAccess = SubscriptionManager.hasAccess(feature);
    
    if (hasAccess) {
        return <>{children}</>;
    }

    return (
        <div className="relative p-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border-2 border-blue-200">
            {/* Preview (blurred) */}
            {children && (
                <div className="absolute inset-0 p-6 pointer-events-none opacity-20 blur-sm overflow-hidden rounded-lg">
                    {children}
                </div>
            )}
            
            {/* Locked Overlay */}
            <div className="relative z-10 text-center py-8">
                <div className="text-6xl mb-4">{icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-700 mb-6 max-w-lg mx-auto">{description}</p>
                <UpgradeButton size="large" />
                <p className="text-sm text-gray-500 mt-4">
                    Subscribe from $14.99/mo — cancel anytime. No free trial on paid plans.
                </p>
            </div>
        </div>
    );
};

// Locked Calculator Card - For calculator selection page
export const LockedCalculatorCard = ({ 
    title, 
    description, 
    feature,
    color = 'blue' 
}) => {
    return (
        <div className={`bg-white rounded-lg shadow-md border-2 border-${color}-200 relative overflow-hidden min-h-[230px] flex flex-col`}>
            {/* Paid Feature Badge */}
            <div className="absolute top-4 right-4 z-10">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    PAID
                </span>
            </div>

            <div className="p-6 opacity-40 flex-1">
                <div className="flex items-center mb-4">
                    <div className={`w-3 h-3 rounded-full bg-${color}-500 mr-3`}></div>
                    <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{description}</p>
                
                {/* Dummy content to show what they're missing */}
                <div className="space-y-3 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Advanced features included</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Professional analysis tools</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Comprehensive reporting</span>
                    </div>
                </div>
            </div>

            {/* Locked Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-white/85 to-blue-50/85 backdrop-blur-sm">
                <div className="text-center p-8 max-w-sm">
                    <div className="text-6xl mb-4">🔒</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Paid Feature</h4>
                    <p className="text-sm text-gray-700 mb-6 leading-relaxed">
                        {SubscriptionManager.getUpgradeMessage(feature)}
                    </p>
                    <UpgradeButton size="large" />
                    <p className="text-xs text-gray-500 mt-4">
                        Click to see pre-populated demo with real outputs
                    </p>
                </div>
            </div>
        </div>
    );
};

// Upgrade Button Component
export const UpgradeButton = ({ size = 'normal', variant = 'primary' }) => {
    const [showPricing, setShowPricing] = useState(false);

    const sizeClasses = {
        small: 'px-4 py-2 text-sm',
        normal: 'px-6 py-3 text-base',
        large: 'px-8 py-4 text-lg'
    };

    const variantClasses = {
        primary: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg',
        secondary: 'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
    };

    return (
        <>
            <button
                onClick={() => setShowPricing(true)}
                className={`${sizeClasses[size]} ${variantClasses[variant]} rounded-lg font-semibold transition-all transform hover:scale-105`}
            >
                Keep testing this plan — Subscribe
            </button>

            {/* Pricing Modal */}
            {showPricing && (
                <PricingModal
                    context={normalizePricingContext({
                        source: 'premium_feature_gate',
                        calculatorTier: 'unknown',
                        surface: 'locked_feature_overlay'
                    })}
                    onClose={() => setShowPricing(false)}
                />
            )}
        </>
    );
};

const PricingModalUserQuotes = () => (
    <div className="mb-6 border border-gray-200 bg-gray-50 rounded-lg p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center mb-3">What users say</p>
        <div className="space-y-3">
            {USER_TESTIMONIALS.map((t, i) => (
                <blockquote key={i} className="text-sm text-gray-700 italic border-l-4 border-blue-400 pl-3 m-0">
                    &ldquo;{t.quote}&rdquo;
                    <footer className="text-xs font-semibold text-gray-600 not-italic mt-1">&mdash; {t.attribution}</footer>
                </blockquote>
            ))}
        </div>
        <p className="text-xs text-gray-500 text-center mt-3">Quotes used with permission. Individual experiences only.</p>
    </div>
);

const PRICING_PLANS = [
    { tier: 'essential', billing: 'monthly', price: 14.99, label: 'Advanced Retirement Monthly', period: '/month' },
    { tier: 'essential', billing: 'annual', price: 149, label: 'Advanced Retirement Annual', period: '/year', save: 30 },
    { tier: 'premium', billing: 'monthly', price: 39.99, label: 'All Access Monthly', period: '/month' },
    { tier: 'premium', billing: 'annual', price: 399, label: 'All Access Annual', period: '/year', save: 80 }
];

// Pricing Modal — simple recommended checkout first, full plan grid on demand
export const PricingModal = ({ onClose, context = null }) => {
    const [couponCode, setCouponCode] = useState('');
    const [couponValid, setCouponValid] = useState(null); // null = not checked, true = valid, false = invalid
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [showAllPlans, setShowAllPlans] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [showTrustDetails, setShowTrustDetails] = useState(false);
    // Monthly default; Annual is a peer switch on the same control (not a skip-to-Stripe link).
    const [selectedBilling, setSelectedBilling] = useState('monthly');
    const pricingContext = normalizePricingContext(context || {});

    // Always lead with Advanced Retirement. SMSF/All Access stays secondary — almost no traffic converts there.
    const funnelSource = pricingContext.source;
    const funnelTier = pricingContext.calculatorTier;
    const fromSmsfGate = pricingContext.source === 'smsf_gate';
    const isHotCheckout = HOT_CHECKOUT_SOURCES.has(funnelSource);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'pricing_modal_open', { event_category: 'conversion', event_label: pricingContext.source, value: 1 });
        }
        trackPricingOpened({
            source: pricingContext.source,
            calculatorTier: pricingContext.calculatorTier,
            surface: pricingContext.surface,
            pricingView: pricingContext.pricingView
        });
    }, [pricingContext.source, pricingContext.calculatorTier, pricingContext.surface, pricingContext.pricingView]);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'pricing_modal_checkout_visible', {
                event_category: 'funnel',
                source: pricingContext.source,
                calculator_tier: pricingContext.calculatorTier,
                surface: pricingContext.surface,
                pricing_view: showAllPlans ? 'full' : 'simple',
                recommended_tier: 'essential',
                recommended_billing: 'monthly',
                hot_checkout: isHotCheckout,
                value: 1
            });
        }
    }, [pricingContext.source, pricingContext.calculatorTier, pricingContext.surface, showAllPlans, isHotCheckout]);

    // Do not prefetch Stripe Checkout sessions on modal open. Prefetch created a real
    // session per open (including ?open_pricing=1 from static CTAs/crawlers), which expired
    // unpaid by the thousand and drowned real conversion signal.

    const contextCopy = (() => {
        switch (pricingContext.source) {
            case 'post_result':
            case 'post_result_dismissed':
                return {
                    title: 'Next steps for this plan',
                    subtitle: 'Stress more start years, export PDF, and save scenarios — from $14.99/mo.'
                };
            case 'basic_results_primary':
            case 'basic_delayed_upsell':
                return {
                    title: 'Stress-test this plan in Advanced',
                    subtitle: 'Bad markets, Age Pension rules, PDF export, and saved scenarios — from $14.99/mo.'
                };
            case 'advanced_gate':
                return {
                    title: 'Keep testing this retirement plan',
                    subtitle: 'Try more start years, change assumptions, export PDF — from $14.99/mo.'
                };
            case 'free_runs_banner':
                return {
                    title: 'After your free run',
                    subtitle: 'Unlock more start-year stress tests, PDF, and saved scenarios — from $14.99/mo.'
                };
            case 'email_gate':
                return {
                    title: 'Keep going when you are ready',
                    subtitle: 'Use your extra free run first — or unlock full stress-testing from $14.99/mo.'
                };
            case 'saved_scenario':
                return {
                    title: 'Restore and keep testing',
                    subtitle: 'Bring back your saved plan, re-run what-ifs, export PDF — from $14.99/mo.'
                };
            case 'advanced_demo':
                return {
                    title: 'Run Advanced with your numbers',
                    subtitle: 'Your free run is for your plan — subscribe for PDF and more stress tests from $14.99/mo.'
                };
            case 'smsf_gate':
                return {
                    title: 'Unlock the SMSF toolkit',
                    subtitle: 'All Access adds the SMSF suite for trustees.'
                };
            case 'pdf_export':
                return {
                    title: 'Export your full report',
                    subtitle: 'PDF with year-by-year detail for your partner or adviser.'
                };
            default:
                return {
                    title: 'Advanced Retirement',
                    subtitle: 'A decision tool: stress-test, export PDF, save scenarios — from $14.99/mo.'
                };
        }
    })();

    const validateCoupon = async (code) => {
        if (!code || code.trim() === '') {
            setCouponValid(null);
            return;
        }

        // LOCALHOST BYPASS: Accept any coupon code on localhost (ONLY on actual localhost, never in production)
        const isLocalhost = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
            !window.location.hostname.includes('netlify') &&
            !window.location.hostname.includes('supercalcpro');
        if (isLocalhost) {
            setCouponValid(true);
            return;
        }

        setValidatingCoupon(true);
        try {
            // Validate coupon by attempting to retrieve it from Stripe
            const response = await fetch('/.netlify/functions/validate-coupon', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ couponCode: code.trim() })
            });

            if (response.ok) {
                const data = await response.json();
                setCouponValid(data.valid);
            } else {
                setCouponValid(false);
            }
        } catch (error) {
            console.error('Coupon validation error:', error);
            setCouponValid(false);
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handleCouponChange = (e) => {
        const code = e.target.value;
        setCouponCode(code);
        // Validate after user stops typing (debounce)
        clearTimeout(window.couponValidationTimeout);
        window.couponValidationTimeout = setTimeout(() => {
            validateCoupon(code);
        }, 500);
    };

    const isLoadingPlan = (tier, billing) =>
        checkoutLoading?.tier === tier && checkoutLoading?.billing === billing;

    const checkoutButtonLabel = (tier, billing, defaultLabel) => {
        if (isLoadingPlan(tier, billing)) return 'Opening secure checkout…';
        return defaultLabel;
    };

    const handleClose = () => {
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'pricing_modal_closed', {
                event_category: 'funnel',
                source: funnelSource,
                calculator_tier: funnelTier,
                pricing_view: showAllPlans ? 'full' : 'simple',
                checkout_loading: !!checkoutLoading,
                value: 1
            });
        }
        onClose();
    };

    const handleClick = async (tier, billing, view = showAllPlans ? 'full' : (isHotCheckout ? 'hot' : 'simple')) => {
        if (checkoutLoading) return;
        setCheckoutLoading({ tier, billing });
        try {
            trackPricingPlanClick({
                tier,
                billing,
                source: funnelSource,
                calculatorTier: funnelTier,
                view,
                surface: 'pricing_modal'
            });
            if (window.AnalyticsManager) {
                window.AnalyticsManager.trackEvent('upgrade_clicked', { tier, billing, hasCoupon: !!couponCode });
            }

            // Special handling for Family100 coupon - bypass Stripe and grant direct access
            const trimmedCoupon = (couponCode || '').trim();
            if (trimmedCoupon.toLowerCase() === 'family100' && couponValid === true) {
                // Grant premium access directly without Stripe
                SubscriptionManager.upgradeToProTier({
                    subscriptionId: `family100-${Date.now()}`,
                    billingCycle: 'annual', // Family100 is treated as annual
                    tier: 'premium'
                });

                // Track the free family access
                if (window.AnalyticsManager) {
                    window.AnalyticsManager.trackEvent('family100_activated', { tier, billing });
                }

                // Show success message
                alert('🎉 Family100 coupon activated! You now have All Access. Welcome to SuperCalc Pro!');
                
                // Close modal and reload page to show premium features
                onClose();
                window.location.reload();
                return;
            }

            await SubscriptionManager.subscribe(tier, billing, '', trimmedCoupon || null, {
                source: funnelSource,
                calculator_tier: funnelTier,
                surface: 'pricing_modal',
                pricing_view: view
            });
        } catch (error) {
            console.error('Subscription error:', error);
            alert('Unable to start checkout. Please try again or contact support@supercalcpro.com.au');
            setCheckoutLoading(null);
        }
    };

    const essentialFeatures = [
        'Everything in Free Calculators',
        'Full Advanced Calculator & professional workflows',
        'Phased Retirement (couples retire at different ages)',
        'Monte Carlo Simulations (1,000 scenarios)',
        '98 Years Historical Data (GFC, crashes, stagflation)',
        '4 Withdrawal Strategies (vs 1 in free tools)',
        'Sequence of Returns Risk Analysis',
        'Transfer Balance Cap Optimization',
        'PDF Export & Detailed Reports'
    ];

    const premiumFeatures = [
        'Everything in Advanced Retirement',
        'SMSF Suite (36 specialized tools)',
        'Real-time Compliance Monitoring',
        'ATO Reporting Examples',
        'Asset Valuation Tools',
        'Member Management',
        'Priority Support'
    ];

    const renderPlanGrid = () => {
        const advancedPlans = PRICING_PLANS.filter((p) => p.tier === 'essential');
        const smsfPlans = PRICING_PLANS.filter((p) => p.tier === 'premium');

        const renderCard = (plan, { muted = false } = {}) => {
            const features = plan.tier === 'essential' ? essentialFeatures : premiumFeatures;
            const loading = isLoadingPlan(plan.tier, plan.billing);
            const billingLabel = plan.billing === 'monthly' ? 'Monthly' : 'Annual';

            return (
                <div
                    key={`${plan.tier}-${plan.billing}`}
                    className={`rounded-lg p-6 transition relative ${
                        muted
                            ? 'border border-gray-200 bg-gray-50 hover:border-gray-300'
                            : 'border-2 border-blue-500 hover:border-blue-600 hover:shadow-lg bg-white'
                    }`}
                >
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${muted ? 'text-gray-500' : 'text-blue-800'}`}>
                        {muted ? 'SMSF add-on' : billingLabel}
                    </p>
                    <h3 className={`font-bold mb-2 ${muted ? 'text-xl text-gray-800' : 'text-2xl text-gray-900'}`}>
                        {plan.tier === 'essential' ? 'Advanced Retirement' : 'All Access'}
                        {muted ? ` · ${billingLabel}` : ''}
                    </h3>
                    <div className={`font-bold text-gray-900 mb-1 ${muted ? 'text-3xl' : 'text-4xl'}`}>
                        ${plan.price}<span className="text-lg text-gray-600 font-normal">{plan.period}</span>
                    </div>
                    {plan.billing === 'annual' && plan.save && (
                        <div className={`mb-4 ${muted ? '' : 'rounded-lg bg-green-50 border border-green-200 px-3 py-2'}`}>
                            <p className={`font-semibold ${muted ? 'text-green-600 text-sm' : 'text-green-700 text-sm'}`}>
                                Save ${plan.save} · ~2 months free
                            </p>
                            {!muted && plan.tier === 'essential' && (
                                <p className="text-xs text-green-800 mt-0.5">
                                    <span className="line-through text-gray-400">$179.88</span>
                                    {' '}→ $12.42/mo effective
                                </p>
                            )}
                            {!muted && plan.tier === 'premium' && (
                                <p className="text-xs text-green-800 mt-0.5">
                                    <span className="line-through text-gray-400">$479.88</span>
                                    {' '}→ $33.25/mo effective
                                </p>
                            )}
                        </div>
                    )}
                    {plan.billing === 'monthly' && !muted && (
                        <p className="text-xs text-gray-500 mb-4">Cancel anytime · start small</p>
                    )}
                    {couponValid === true && (
                        <p className="text-blue-600 font-semibold text-sm mb-4">
                            {couponCode.trim().toLowerCase() === 'family100'
                                ? '🎉 FREE All Access with Family100 — no payment required'
                                : 'Discount applied at checkout'}
                        </p>
                    )}
                    <ul className="space-y-2 my-4 text-sm max-h-40 overflow-y-auto">
                        {features.slice(0, muted ? 4 : 6).map((feature, fIdx) => (
                            <li key={fIdx} className="flex items-start gap-2">
                                <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${muted ? 'text-gray-400' : 'text-blue-500'}`} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className={muted ? 'text-gray-600' : 'text-gray-700'}>{feature}</span>
                            </li>
                        ))}
                    </ul>
                    <button
                        type="button"
                        onClick={() => handleClick(plan.tier, plan.billing, 'full')}
                        disabled={!!checkoutLoading}
                        data-subscription-btn
                        className={`w-full font-bold py-3 rounded-lg transition disabled:opacity-70 disabled:cursor-wait ${
                            muted
                                ? 'bg-white border border-gray-300 hover:bg-gray-100 text-gray-800'
                                : 'bg-blue-600 hover:bg-blue-700 text-white shadow'
                        }`}
                    >
                        {checkoutButtonLabel(
                            plan.tier,
                            plan.billing,
                            muted
                                ? `All Access — $${plan.price}${plan.period}`
                                : plan.billing === 'annual' && plan.save
                                    ? `Start annual — $${plan.price} (save $${plan.save})`
                                    : `Start monthly — $${plan.price}`
                        )}
                    </button>
                    {loading && (
                        <p className="text-xs text-gray-500 text-center mt-2">Redirecting to Stripe…</p>
                    )}
                </div>
            );
        };

        return (
            <div className="space-y-8">
                <div>
                    <h4 className="text-sm font-semibold text-blue-800 uppercase tracking-wide mb-3 text-center">
                        Advanced Retirement — pick monthly or annual
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {advancedPlans.map((plan) => renderCard(plan))}
                    </div>
                </div>
                <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-1 text-center">
                        All Access (SMSF suite)
                    </h4>
                    <p className="text-xs text-gray-400 mb-4 text-center max-w-md mx-auto">
                        Only needed if you run an SMSF. Most subscribers only need Advanced.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                        {smsfPlans.map((plan) => renderCard(plan, { muted: true }))}
                    </div>
                </div>
            </div>
        );
    };

    const couponField = (
        <details className="mb-6 max-w-md mx-auto group">
            <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900 text-center list-none">
                Have a coupon code?
            </summary>
            <div className="mt-3">
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={couponCode}
                        onChange={handleCouponChange}
                        placeholder="Enter code"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {validatingCoupon && (
                        <div className="flex items-center px-4">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                        </div>
                    )}
                    {couponValid === true && (
                        <div className="flex items-center px-4 text-green-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                    )}
                    {couponValid === false && (
                        <div className="flex items-center px-4 text-red-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                    )}
                </div>
                {couponValid === true && (
                    <p className="mt-2 text-sm text-green-600 text-center">
                        {couponCode.trim().toLowerCase() === 'family100'
                            ? 'Family100 valid — free All Access when you continue'
                            : 'Coupon valid — discount at checkout'}
                    </p>
                )}
                {couponValid === false && (
                    <p className="mt-2 text-sm text-red-600 text-center">Invalid coupon code</p>
                )}
            </div>
        </details>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4" onClick={handleClose}>
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className={`bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-xl ${isHotCheckout ? 'p-5' : 'p-6 md:p-7'}`}>
                    <div className="flex justify-between items-start gap-4">
                        <div>
                            <h2 className={`font-bold mb-1 ${isHotCheckout ? 'text-xl' : 'text-2xl'}`}>{contextCopy.title}</h2>
                            <p className={`text-blue-100 ${isHotCheckout ? 'text-sm' : 'text-base'}`}>{contextCopy.subtitle}</p>
                        </div>
                        <button type="button" onClick={handleClose} aria-label="Close" className="text-white hover:text-gray-200 text-3xl leading-none flex-shrink-0">×</button>
                    </div>
                </div>

                <div className={`p-5 md:p-6 ${isHotCheckout ? 'pt-4' : ''}`}>
                    {!showAllPlans && (
                        <div className="mb-4">
                            <div className="rounded-xl border-2 border-blue-600 bg-blue-50/30 p-5">
                                <div className="text-center mb-4">
                                    <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">Advanced Retirement</p>
                                    <div
                                        className="inline-flex rounded-lg border border-blue-200 bg-white p-1 mb-3"
                                        role="group"
                                        aria-label="Billing period"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setSelectedBilling('monthly')}
                                            disabled={!!checkoutLoading}
                                            aria-pressed={selectedBilling === 'monthly'}
                                            className={`px-4 py-2 text-sm font-semibold rounded-md transition ${
                                                selectedBilling === 'monthly'
                                                    ? 'bg-blue-600 text-white shadow'
                                                    : 'text-gray-700 hover:bg-blue-50'
                                            }`}
                                        >
                                            Monthly
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedBilling('annual')}
                                            disabled={!!checkoutLoading}
                                            aria-pressed={selectedBilling === 'annual'}
                                            className={`px-4 py-2 text-sm font-semibold rounded-md transition ${
                                                selectedBilling === 'annual'
                                                    ? 'bg-blue-600 text-white shadow'
                                                    : 'text-gray-700 hover:bg-blue-50'
                                            }`}
                                        >
                                            Annual
                                        </button>
                                    </div>
                                    <div className="text-4xl font-bold text-gray-900">
                                        {selectedBilling === 'annual' ? (
                                            <>
                                                $149<span className="text-base text-gray-600 font-normal">/yr</span>
                                            </>
                                        ) : (
                                            <>
                                                $14.99<span className="text-base text-gray-600 font-normal">/mo</span>
                                            </>
                                        )}
                                    </div>
                                    {selectedBilling === 'annual' ? (
                                        <p className="text-sm text-green-700 font-medium mt-1">Save $30 vs monthly</p>
                                    ) : null}
                                    <p className="text-sm text-gray-700 mt-2">Stress-test, export PDF, and save scenarios for your retirement decision.</p>
                                    <p className="text-xs text-gray-500 mt-1">Cancel anytime · Secure Stripe checkout · 30-day money-back</p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => handleClick('essential', selectedBilling, isHotCheckout ? 'hot' : 'simple')}
                                    disabled={!!checkoutLoading}
                                    data-subscription-btn
                                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-wait text-white font-bold py-4 px-4 rounded-lg text-lg transition shadow"
                                >
                                    {checkoutButtonLabel(
                                        'essential',
                                        selectedBilling,
                                        selectedBilling === 'annual'
                                            ? (isHotCheckout ? 'Continue to checkout — $149/yr' : 'Start annual — $149')
                                            : (isHotCheckout ? 'Continue to checkout — $14.99/mo' : 'Start monthly — $14.99')
                                    )}
                                </button>

                                {checkoutLoading && (
                                    <p className="text-sm text-gray-500 mt-3 text-center">You&apos;ll be redirected to Stripe to complete payment.</p>
                                )}
                            </div>

                            {!isHotCheckout && (
                                <p className="text-center mt-3 text-xs text-gray-500">
                                    Includes phased retirement for couples — most Australian retirements.
                                </p>
                            )}

                            <p className="text-center mt-4 text-xs text-gray-400">
                                SMSF trustee?{' '}
                                <button
                                    type="button"
                                    onClick={() => setShowAllPlans(true)}
                                    className="text-gray-500 hover:text-gray-700 underline"
                                >
                                    All Access plans
                                </button>
                            </p>
                        </div>
                    )}

                    {showAllPlans && (
                        <>
                            <div className="mb-4 flex items-center justify-between gap-2">
                                <h3 className="text-lg font-semibold text-gray-900">All plans</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowAllPlans(false)}
                                    className="text-sm text-blue-600 hover:underline"
                                >
                                    Back to Advanced pricing
                                </button>
                            </div>
                            <p className="text-sm text-gray-500 mb-4">
                                Advanced Retirement is the main product. All Access adds the SMSF suite for trustees who need it.
                            </p>
                            <div className="mb-6 overflow-x-auto">
                                <table className="w-full text-sm border border-gray-200 rounded-lg">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="text-left p-3 font-semibold text-gray-700">Feature</th>
                                            <th className="text-center p-3 font-semibold text-gray-700">Advanced</th>
                                            <th className="text-center p-3 font-semibold text-gray-700">All Access</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600">
                                        <tr className="border-t border-gray-200"><td className="p-3">Monte Carlo + historical backtest</td><td className="p-3 text-center">✓</td><td className="p-3 text-center">✓</td></tr>
                                        <tr className="border-t border-gray-200"><td className="p-3">Phased retirement</td><td className="p-3 text-center">✓</td><td className="p-3 text-center">✓</td></tr>
                                        <tr className="border-t border-gray-200"><td className="p-3">PDF export</td><td className="p-3 text-center">✓</td><td className="p-3 text-center">✓</td></tr>
                                        <tr className="border-t border-gray-200"><td className="p-3">SMSF suite (36 tools)</td><td className="p-3 text-center">—</td><td className="p-3 text-center">✓</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            {renderPlanGrid()}
                        </>
                    )}

                    {couponField}

                    {/* Trust / social proof below the CTA so it does not compete with checkout */}
                    {!showAllPlans && (
                        <div className="mt-2 border-t border-gray-100 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowTrustDetails((v) => !v)}
                                className="w-full text-left text-sm text-gray-600 hover:text-gray-900 flex items-center justify-between gap-2"
                            >
                                <span>Why people subscribe</span>
                                <span className="text-gray-400">{showTrustDetails ? '−' : '+'}</span>
                            </button>
                            {showTrustDetails && (
                                <div className="mt-3 space-y-3">
                                    <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                                        <p className="m-0">
                                            <strong>Unsolicited coverage in Super Review:</strong> early-retirement modelling covered after finding a couple retiring at 60 needed $955,000 in poor markets — not just the age-67 benchmark.
                                            {' '}
                                            <a
                                                href="https://www.superreview.com.au/does-asfas-retirement-target-fall-short-for-early-retirees/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-semibold underline"
                                            >
                                                Read the coverage
                                            </a>
                                        </p>
                                    </div>
                                    <PricingModalUserQuotes />
                                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-700">
                                        <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 text-center">
                                            <strong className="block text-blue-800">Historical</strong>
                                            1929–2008 crashes
                                        </div>
                                        <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 text-center">
                                            <strong className="block text-blue-800">Monte Carlo</strong>
                                            Survival odds
                                        </div>
                                        <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 text-center">
                                            <strong className="block text-blue-800">Reports</strong>
                                            Save &amp; export
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <p className="text-xs text-gray-500 text-center mt-4">
                        Paid already but still locked?{' '}
                        <a href="/restore-access.html" className="underline">Restore Access</a>
                        {' '}with your checkout email.
                    </p>
                </div>
            </div>
        </div>
    );
};

// Subscription Status Badge (for header/nav)
export const SubscriptionBadge = () => {
    const [showManagement, setShowManagement] = useState(false);
    const info = SubscriptionManager.getSubscriptionInfo();

    if (info.isPro) {
        const openManagement = () => setShowManagement(true);
        return (
            <>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
                    <button
                        type="button"
                        onClick={openManagement}
                        className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-semibold shadow hover:from-blue-700 hover:to-purple-700 transition"
                        title="Your plan, payment method, and receipts"
                    >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span>PRO{info.isOnTrial ? ' (Trial)' : ''}</span>
                    </button>
                    <button
                        type="button"
                        onClick={openManagement}
                        className="text-xs text-gray-500 hover:text-gray-800 underline decoration-gray-400/70 underline-offset-2 font-medium"
                        title="Update card, invoices, or cancel at period end"
                    >
                        Manage billing
                    </button>
                </div>
                {showManagement && (
                    <SubscriptionManagementModal onClose={() => setShowManagement(false)} />
                )}
            </>
        );
    }

    return (
        <button
            onClick={() => openPricingModal({ source: 'nav_upgrade', calculatorTier: 'unknown' })}
            className="flex items-center gap-2 px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm font-semibold transition"
        >
            <span>Free Calculators</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
        </button>
    );
};

// Subscription Management Modal Wrapper
const SubscriptionManagementModal = ({ onClose }) => {
    return (
        <Suspense fallback={
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            </div>
        }>
            <SubscriptionManagement onClose={onClose} />
        </Suspense>
    );
};

// Feature Teaser - Shows what user is missing
export const FeatureTeaser = ({ features = [] }) => {
    return (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border-2 border-blue-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="text-2xl">✨</span>
                Unlock Advanced Retirement and All Access Features
            </h3>
            <ul className="space-y-2 mb-4">
                {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">{feature}</span>
                    </li>
                ))}
            </ul>
            <UpgradeButton size="normal" />
        </div>
    );
};

export default {
    PremiumFeatureGate,
    LockedInput,
    LockedSection,
    LockedCalculatorCard,
    UpgradeButton,
    SubscriptionBadge,
    PricingModal,
    FeatureTeaser
};

